/**
 * Danish twins for platform-chrome strings (SBX-537 FE14).
 *
 * The module ships its catalog through
 * `src/modules/route-studio/i18n/register.ts`, but that seed rides
 * the module chunk - chrome surfaces (login page, the app-level
 * error boundary) render before or without it. Chrome files that
 * own user-facing strings seed their own twins here with the same
 * FE8-2 discipline: catalog-first, CDS-wins - a reviewed upstream
 * translation for the same source string always beats the shipped
 * one, and seeding never overwrites what the CDS already delivered.
 */
import { FETCHING_TRANSLATIONS, onEvent, tx } from '@transifex/native'

const seed = (
  localeCode: string,
  catalogs: Record<string, Record<string, string>>
) => {
  const catalog = catalogs[localeCode]
  if (!catalog) return
  const existing: Record<string, string> = tx.cache.getTranslations(localeCode)
  const additions: Record<string, string> = {}
  Object.entries(catalog).forEach(([source, translation]) => {
    if (!(source in existing)) additions[source] = translation
  })
  if (Object.keys(additions).length > 0) {
    tx.cache.update(localeCode, additions)
  }
}

/**
 * Hook a chrome-owned catalog into the platform's Transifex cache.
 * Call once at module scope of the file that owns the strings;
 * idempotent in effect (seeding twice adds nothing new).
 */
export function seedChromeCatalog(
  catalogs: Record<string, Record<string, string>>
): void {
  onEvent(FETCHING_TRANSLATIONS, (payload: unknown) => {
    const localeCode = (payload as { localeCode?: string } | null)?.localeCode
    if (localeCode) seed(localeCode, catalogs)
  })
  // the locale can already be live when this chrome chunk loads
  Object.keys(catalogs).forEach((localeCode) => {
    if (
      tx.cache.hasTranslations(localeCode) ||
      tx.getCurrentLocale() === localeCode
    ) {
      seed(localeCode, catalogs)
    }
  })
}
